import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSiteMedia } from "../context/SiteMedia";
import { CONTACT_EMAIL } from "../i18n/translations";
import { IMAGES } from "../assets/images";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import IntakeForm from "../components/IntakeForm";

const audienceImages = [IMAGES.creativeTraining, IMAGES.womenStrength, IMAGES.execTraining];
const galleryImages = [IMAGES.social1, IMAGES.social2, IMAGES.social3, IMAGES.social4];

export default function Home() {
  const { t } = useLanguage();
  const { portraitUrl, gallery: uploadedGallery } = useSiteMedia();
  const galleryItems =
    uploadedGallery.length > 0
      ? uploadedGallery.map((g) => ({ src: g.url, caption: g.alt || "Pierre Coaching" }))
      : galleryImages.map((src, i) => ({ src, caption: t.gallery.captions[i] }));

  return (
    <>
      <Seo title={t.homeSeo.title} description={t.homeSeo.desc} />

      {/* HERO */}
      <section
        className="pt-16 pb-20 md:py-28 border-b border-line-subtle relative overflow-hidden"
        id="hero"
        data-testid="hero-section"
      >
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-gold/5 blur-3xl pointer-events-none d1-drift" />

        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-8">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 mb-6 text-xs font-mono tracking-widest uppercase rounded border border-gold/30 text-gold bg-coal-badge"
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              {t.hero.badge}
            </div>

            <h1
              className="font-display uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight mb-8 text-white"
              data-testid="hero-title"
            >
              <span className="ln">
                <span>{t.hero.titleTop}</span>
              </span>
              <span className="ln">
                <span className="text-gold">{t.hero.titleBottom}</span>
              </span>
            </h1>

            <p
              className="text-base sm:text-lg text-bone-soft max-w-xl leading-relaxed mb-10 d1-reveal"
              data-testid="hero-subline"
            >
              {t.hero.sub}
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 d1-reveal">
              <a
                href="#intake"
                className="inline-flex justify-center items-center px-7 py-4 text-sm font-semibold tracking-wider uppercase bg-gold text-black rounded hover:bg-gold-bright transition-colors"
                data-testid="hero-primary-cta"
              >
                {t.hero.ctaPrimary}
              </a>
              <Link
                to="/about"
                className="inline-flex justify-center items-center px-7 py-4 text-sm font-semibold tracking-wider uppercase border border-line-strong text-bone-soft rounded hover:border-gold hover:text-white transition-colors"
                data-testid="hero-secondary-cta"
              >
                {t.hero.ctaSecondary}
              </Link>
            </div>
          </div>

          <div className="md:col-span-4 flex flex-col justify-between border-t md:border-t-0 md:border-l border-line-subtle md:pl-8 pt-8 md:pt-0 gap-6">
            <div className="bg-coal-card p-6 rounded-lg border border-line-subtle" data-testid="hero-email-card">
              <div className="text-xs font-mono uppercase tracking-widest text-gold-dim mb-1">
                {t.hero.cardEmailLabel}
              </div>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-sm font-mono text-white break-all select-all font-medium hover:text-gold transition-colors"
                data-testid="hero-email-link"
              >
                {CONTACT_EMAIL}
              </a>
            </div>

            <div className="bg-coal-card p-6 rounded-lg border border-line-subtle" data-testid="hero-format-card">
              <div className="text-xs font-mono uppercase tracking-widest text-gold-dim mb-1">
                {t.hero.cardFormatLabel}
              </div>
              <div className="text-sm text-bone-soft">{t.hero.cardFormatValue}</div>
            </div>

            <div className="bg-coal-card p-6 rounded-lg border border-line-subtle" data-testid="hero-safety-card">
              <div className="text-xs font-mono uppercase tracking-widest text-gold-dim mb-1">
                {t.hero.cardSafetyLabel}
              </div>
              <div className="text-sm text-bone-soft">{t.hero.cardSafetyValue}</div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* ABOUT / ANDRE */}
      <section className="py-24 border-b border-line-subtle" id="about" data-testid="about-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <Reveal className="md:col-span-5">
              <figure className="relative aspect-[4/5] rounded-lg border border-line-strong overflow-hidden">
                <img
                  src={portraitUrl || IMAGES.coachPortrait}
                  alt={t.about.portraitCaption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="coach-portrait-img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <figcaption className="absolute bottom-0 left-0 p-6 z-10 text-xs font-mono uppercase tracking-widest text-gold">
                  {t.about.portraitCaption}
                </figcaption>
              </figure>
            </Reveal>

            <Reveal className="md:col-span-7" delay={120}>
              <span className="text-xs font-mono uppercase tracking-widest text-gold">
                {t.about.eyebrow}
              </span>
              <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mt-2 mb-6">
                {t.about.title}
              </h2>
              <p className="text-base text-bone-soft leading-relaxed mb-6" data-testid="about-quote">
                {t.about.quote}
              </p>

              <div className="grid grid-cols-2 gap-4 pt-6 border-t border-line-subtle">
                <div>
                  <div className="text-2xl font-display text-gold">{t.about.fact1Value}</div>
                  <div className="text-xs text-bone-mute mt-1">{t.about.fact1Label}</div>
                </div>
                <div>
                  <div className="text-2xl font-display text-gold">{t.about.fact2Value}</div>
                  <div className="text-xs text-bone-mute mt-1">{t.about.fact2Label}</div>
                </div>
              </div>

              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-8 text-xs font-mono uppercase tracking-widest text-gold hover:text-gold-bright transition-colors"
                data-testid="about-more-link"
              >
                {t.about.moreLink}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AUDIENCE TRACKS */}
      <section className="bg-coal-raise py-24 border-b border-line-subtle" id="audience" data-testid="audience-section">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="max-w-2xl mb-16">
            <span className="text-xs font-mono uppercase tracking-widest text-gold">
              {t.audience.eyebrow}
            </span>
            <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-white mt-2">
              {t.audience.title}
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {t.audience.cards.map((card, i) => (
              <Reveal key={card.tag} delay={i * 120}>
                <div
                  className="bg-coal-hover p-8 rounded-lg border border-line hover:border-gold/50 transition-colors h-full"
                  data-testid={`audience-card-${i + 1}`}
                >
                  <div className="text-xs font-mono uppercase text-gold mb-4">{card.tag}</div>
                  <h3 className="font-display text-2xl uppercase text-white mb-3">{card.title}</h3>
                  <p className="text-sm text-bone-soft leading-relaxed mb-6">{card.body}</p>
                  <figure className="relative aspect-[16/9] rounded border border-line-strong overflow-hidden">
                    <img
                      src={audienceImages[i]}
                      alt={card.caption}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <figcaption className="absolute bottom-0 left-0 p-3 text-[10px] font-mono uppercase text-bone-soft">
                      {card.caption}
                    </figcaption>
                  </figure>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL GALLERY + HONEST PROOF */}
      <section className="py-24 border-b border-line-subtle" id="gallery" data-testid="gallery-section">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between mb-12">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-gold">
                {t.gallery.eyebrow}
              </span>
              <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-white mt-2">
                {t.gallery.title}
              </h2>
            </div>
            <p className="text-xs font-mono text-bone-mute uppercase mt-2 sm:mt-0">
              {t.gallery.handle}
            </p>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {galleryItems.map((item, i) => (
              <Reveal key={item.src} delay={i * 100}>
                <figure className="relative aspect-square rounded border border-line overflow-hidden group">
                  <img
                    src={item.src}
                    alt={item.caption}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    data-testid={`gallery-img-${i + 1}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <figcaption className="absolute bottom-0 left-0 p-4 text-[10px] font-mono uppercase text-bone-soft">
                    {item.caption}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div
              className="bg-coal-raise border border-line rounded-lg p-8 text-center max-w-2xl mx-auto"
              data-testid="proof-placeholder"
            >
              <span className="text-xs font-mono uppercase tracking-widest text-gold-dim">
                {t.gallery.proofEyebrow}
              </span>
              <h3 className="font-display uppercase text-xl text-white mt-2 mb-2">
                {t.gallery.proofTitle}
              </h3>
              <p className="text-sm text-bone-mute">{t.gallery.proofBody}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTAKE FORM */}
      <section className="bg-coal-panel py-24" id="intake" data-testid="intake-section">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal className="text-center mb-12">
            <span className="text-xs font-mono uppercase tracking-widest text-gold">
              {t.intake.eyebrow}
            </span>
            <h2 className="font-display uppercase text-3xl sm:text-5xl tracking-tight text-white mt-2 mb-4">
              {t.intake.title}
            </h2>
            <p className="text-sm text-bone-soft max-w-lg mx-auto">{t.intake.sub}</p>
          </Reveal>
          <Reveal delay={120}>
            <IntakeForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
